import { createBrowserRouter, Navigate, Outlet } from "react-router"
import App from "~/App"
import { ErrorFallback } from "~/components/ErrorFallback"
import { AuthProvider } from "~/context/auth"
import { AccessControlLayer } from "~/layouts/AccessControlLayer"
import { RequireAuth } from "~/layouts/RequireAuth"
import { setupResponseErrorInterceptor } from "~/lib"
import { AccessDenied } from "~/pages/AccessDenied"
import { SignIn } from "~/pages/SignIn"

export const APPLICATION_BASE = "/vetting"

export enum APP_ROUTES {
  Entity = "entity",
  Organization = "organization",
  SignIn = "sign-in",
  AccessDenied = "access-denied",
}

export enum ENTITY_ROUTES {
  Start = "start",
  ActiveTasks = "active",
  CompletedTasks = "completed",
  OrganizationUpdate = "organization-update",
}

export const START_VETTING_QUERY_PARAMS = {
  PROGRAM: "program",
  GRANT_PROGRAM_ID: "grantProgramId",
  IS_SELF_REFERRAL: "isSelfReferral",
}

/* Setup global error interceptor. Added here
   Register global Axios response error interceptor once during app bootstrap.
   Placed in router setup because:
   - It runs exactly once on initial load (no HMR duplication risk in entry files)
   - It's logically tied to app initialization / routing lifecycle */
setupResponseErrorInterceptor()

export const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <Outlet />,
      errorElement: <ErrorFallback className="h-screen" />,
      children: [
        {
          path: `/${APP_ROUTES.Entity}`,
          element: (
            <AuthProvider>
              <AccessControlLayer>
                <RequireAuth />
              </AccessControlLayer>
            </AuthProvider>
          ),
          children: [
            {
              path: ":id",
              children: [
                {
                  path: "",
                  element: <App />,
                  children: [
                    {
                      index: true,
                      element: <Navigate replace to={ENTITY_ROUTES.ActiveTasks} />,
                    },
                    {
                      path: ENTITY_ROUTES.ActiveTasks,
                      async lazy() {
                        const { EntityActiveTasks } = await import("~/pages/EntityActiveTasks")
                        return { Component: EntityActiveTasks }
                      },
                    },
                    {
                      path: ENTITY_ROUTES.CompletedTasks,
                      async lazy() {
                        const { EntityCompletedTasks } = await import("~/pages/EntityCompletedTasks")
                        return { Component: EntityCompletedTasks }
                      },
                    },
                    {
                      path: ENTITY_ROUTES.OrganizationUpdate,
                      async lazy() {
                        const { EntityOrganizationUpdate } = await import("~/pages/EntityOrganizationUpdate")
                        return { Component: EntityOrganizationUpdate }
                      },
                    },
                  ],
                },
                {
                  path: "",
                  element: <App showNavigation={false} />,
                  children: [
                    {
                      path: ENTITY_ROUTES.Start,
                      async lazy() {
                        const { StartVetting } = await import("~/pages/StartVetting")
                        return { Component: StartVetting }
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: `/${APP_ROUTES.Organization}/:orgId/:page`,
          element: (
            <AuthProvider>
              <AccessControlLayer>
                <RequireAuth />
              </AccessControlLayer>
            </AuthProvider>
          ),
          children: [
            {
              index: true,
              async lazy() {
                const { OrganizationRedirect } = await import("~/pages/OrganizationRedirect")
                return { Component: OrganizationRedirect }
              },
            },
          ],
        },
        {
          path: APP_ROUTES.AccessDenied,
          element: (
            <AuthProvider>
              <RequireAuth />
            </AuthProvider>
          ),
          children: [
            {
              index: true,
              element: <AccessDenied />,
            },
          ],
        },
        {
          path: APP_ROUTES.SignIn,
          element: <SignIn />,
        },
        // Fallback for missing application root
        {
          index: true,
          element: <ErrorFallback className="h-screen" />,
        },
      ],
    },
  ],
  { basename: APPLICATION_BASE }
)
